import React, { useState, useEffect, useMemo, useCallback } from "react";
import stocksApi from "../data/stock";
import updatestocksApi from "../data/stockhistory";
import partiesApi from "../data/party";
import { useSearchParams, useNavigate } from "react-router-dom";

// --- Begin Section borrowed from stocks.tsx --- //
interface StockCategory {
  id?: number;
  category: string;
  stock_name: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
  current_price?: string;
  current_price_date?: string;
}

interface Party {
  id: number;
  date: string;
  name: string;
  number: string;
  contact_person_name: string;
  contact_person_number: string;
  categories: string[];
  deleteparty: number;
}
interface StockRow {
  id: number;
  party_id: number;
  party_name?: string;
  date: string;
  categories: StockCategory[];
  cat_id?: number;
  name?: string;
  category?: string;
  unit?: string;
  price_per_unit?: string | number;
  total_amount?: string | number;
  indirect_charges?: string | number;
  current_price?: string;
  current_price_date?: string;
  sundry_creditors?: string;  // for latest creditor
}
// --- End Section borrowed from stocks.tsx --- //

interface SundryCreditorHistoryEntry {
  id?: number;
  stock_id: number;
  cat_id?: number;
  sundry_creditors?: string;
  current_price_date?: string;
  created_at?: string; // fallback for date
}

// Helper: Get the most recent update record for given stock/cat
function getLatestStockHistory(
  history: any[],
  stock_id: number,
  cat_id?: number
) {
  const items = history.filter(
    (rec) =>
      rec.stock_id === stock_id &&
      String(rec.cat_id ?? "") === String(cat_id ?? "")
  );
  if (items.length === 0) return null;
  return items.reduce((latest, item) => {
    const lt = latest.current_price_date
      ? new Date(latest.current_price_date).getTime()
      : 0;
    const ct = item.current_price_date
      ? new Date(item.current_price_date).getTime()
      : 0;
    if (ct > lt) return item;
    if (ct === lt) {
      return (item.id ?? 0) > (latest.id ?? 0) ? item : latest;
    }
    return latest;
  }, items[0]);
}

// Helper: Get ALL sundry_creditors history for a given stock/cat pair, most recent first
function getAllSundryCreditorsForStock(
  history: any[],
  stock_id: number,
  cat_id?: number
): SundryCreditorHistoryEntry[] {
  return (
    history
      .filter(
        (rec) =>
          rec.stock_id === stock_id &&
          String(rec.cat_id ?? "") === String(cat_id ?? "") &&
          rec.sundry_creditors !== undefined &&
          rec.sundry_creditors !== null &&
          rec.sundry_creditors !== "" &&
          !isNaN(Number(rec.sundry_creditors))
      )
      .sort((a, b) => {
        // Most recent first
        const getDate = (x: any) =>
          x.current_price_date
            ? new Date(x.current_price_date).getTime()
            : x.created_at
            ? new Date(x.created_at).getTime()
            : 0;
        return getDate(b) - getDate(a);
      })
  );
}

// Helper: Unique party filter options
function getUniqueParties(
  rows: StockRow[],
  parties: Party[]
): { party_id: number; party_name?: string }[] {
  const encountered = new Set<number>();
  const result: { party_id: number; party_name?: string }[] = [];
  for (const row of rows) {
    if (!encountered.has(row.party_id)) {
      encountered.add(row.party_id);
      // Use party name from parties, fallback to id label
      const partyObj = parties.find((p) => p.id === row.party_id);
      result.push({
        party_id: row.party_id,
        party_name: partyObj?.name, // May be undefined; handled in render
      });
    }
  }
  return result;
}

// Helper: Unique categories for a given party filter
function getUniqueCategories(
  rows: StockRow[],
  filterParty: number | ""
): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    if (filterParty === "" || row.party_id === filterParty) {
      if (row.category) seen.add(row.category);
    }
  }
  return Array.from(seen);
}

function UpdateStockValuePage() {
  const [rows, setRows] = useState<StockRow[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [selected, setSelected] = useState<StockRow | null>(null);
  const [currentPrice, setCurrentPrice] = useState<string>("");
  const [currentPriceDate, setCurrentPriceDate] = useState<string>("");
  // Instead of holding just one, input for new dividend entry (string)
  const [dividendAmount, setDividendAmount] = useState<string>("");
  const [parties, setParties] = useState<Party[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  // Track all prior sundry_creditors history for current selection
  const [sundryHistory, setSundryHistory] = useState<SundryCreditorHistoryEntry[]>([]);

  // Filters
  const [filterCategory, setFilterCategory] = useState<string>("");

  // Use party_id from search param (?party=12) - but ALWAYS show Party dropdown, selecting 'All Parties' clears the filter
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get party id from search param and sanitise, for default selection
  const partyIdSearchParam = searchParams.get("party");
  // State variable for controlled party filter, default to "party" from url if present, else ""
  const [filterParty, setFilterParty] = useState<number | "">(() => {
    if (
      typeof partyIdSearchParam === "string" &&
      partyIdSearchParam !== "" &&
      !isNaN(Number(partyIdSearchParam))
    ) {
      return Number(partyIdSearchParam);
    }
    return "";
  });

  // Watch search param "party": update filterParty if url changes
  useEffect(() => {
    if (
      typeof partyIdSearchParam === "string" &&
      partyIdSearchParam !== "" &&
      !isNaN(Number(partyIdSearchParam))
    ) {
      setFilterParty(Number(partyIdSearchParam));
    } else {
      setFilterParty("");
    }
    // Don't include setFilterParty in deps to avoid infinite loop
    // eslint-disable-next-line
  }, [partyIdSearchParam]);

  // Fetch update history & parties
  const fetchParties = useCallback(async () => {
    const data = await partiesApi();
    const cleanedData = Array.isArray(data)
      ? data.map((item: any) => ({
          ...item,
          categories:
            typeof item.categories === "string"
              ? (() => {
                  try {
                    const parsed = JSON.parse(item.categories);
                    return Array.isArray(parsed)
                      ? parsed
                      : [item.categories];
                  } catch {
                    return [item.categories];
                  }
                })()
              : Array.isArray(item.categories)
              ? item.categories
              : [],
        }))
      : [];
    setParties(cleanedData);
  }, []);
  useEffect(() => {
    fetchParties();
    const fetchHistory = async () => {
      try {
        const hist = await updatestocksApi();
        setHistory(Array.isArray(hist) ? hist : []);
      } catch {
        setHistory([]);
      }
    };
    fetchHistory();
  }, []);

  // Fetch stocks and flatten rows by category (mimic structure from stocks.tsx)
  useEffect(() => {
    const fetchRows = async () => {
      const data = await stocksApi();
      let flatRows: StockRow[] = [];
      if (Array.isArray(data)) {
        data.forEach((entry: any) => {
          let catObjs: StockCategory[] = [];
          if (typeof entry.categories === "string") {
            try {
              const arr = JSON.parse(entry.categories);
              catObjs = Array.isArray(arr) ? arr : [arr];
            } catch {
              catObjs = [];
            }
          } else if (Array.isArray(entry.categories)) {
            catObjs = entry.categories;
          }
          if (!Array.isArray(catObjs)) catObjs = [];
          if (catObjs.length > 0) {
            catObjs.forEach((catObj) => {
              flatRows.push({
                ...entry,
                categories: [catObj],
                cat_id: catObj.id,
                name: catObj.stock_name,
                category: catObj.category,
                unit: catObj.unit,
                price_per_unit: catObj.perUnitCharge,
                total_amount: catObj.total_amount,
                indirect_charges: catObj.indirect_charges,
                current_price: "",
                current_price_date: "",
                sundry_creditors: "",
              });
            });
          }
        });
      }
      setRows(
        flatRows.map((row) => {
          const last = getLatestStockHistory(history, row.id, row.cat_id);
          return {
            ...row,
            current_price: last?.current_price ?? "",
            current_price_date: last?.current_price_date ?? "",
            sundry_creditors: last?.sundry_creditors ?? "",
          };
        })
      );
    };
    fetchRows();
    // eslint-disable-next-line
  }, [history]);

  // When selected row changes, set up the dividendAmount & sundryHistory
  useEffect(() => {
    if (selected && history.length > 0) {
      // Get all sundry_creditors for selected
      const hist = getAllSundryCreditorsForStock(history, selected.id, selected.cat_id);
      setSundryHistory(hist);
      setDividendAmount(""); // reset input
    } else {
      setSundryHistory([]);
    }
  }, [selected, history]);

  // Memoize unique parties from rows using parties for name
  const uniqueParties = useMemo(
    () => getUniqueParties(rows, parties),
    [rows, parties]
  );
  const uniqueCategories = useMemo(
    () => getUniqueCategories(rows, filterParty),
    [rows, filterParty]
  );

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          (filterParty === "" || r.party_id === filterParty) &&
          (!filterCategory || r.category === filterCategory)
      ),
    [rows, filterParty, filterCategory]
  );

  // Helper: get party name by id, fallback label
  const getPartyNameById = (party_id: number) => {
    const party = parties.find((p) => p.id === party_id);
    if (party && typeof party.name === "string" && party.name.trim()) {
      return party.name;
    }
    return `Party #${party_id}`;
  };

  const handleEdit = (row: StockRow) => {
    setSelected(row);
    setCurrentPrice(row.current_price ?? "");
    setCurrentPriceDate(
      row.current_price_date
        ? typeof row.current_price_date === "string"
          ? row.current_price_date.slice(0, 10)
          : new Date(row.current_price_date).toISOString().slice(0, 10)
        : ""
    );
    // setDividendAmount(''); // will set via effect
  };

  const handleAddDividend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    if (!dividendAmount || isNaN(Number(dividendAmount))) {
      alert("Please enter a valid dividend amount.");
      return;
    }
    setIsUpdating(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/auth/updatestock`, {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stock_id: selected.id,
          cat_id: selected.cat_id,
          // Send only dividend in this submission, but also fill current price info
          current_price: selected.current_price ?? "",
          current_price_date: selected.current_price_date ?? "",
          sundry_creditors: dividendAmount,
        }),
      });
      setDividendAmount("");
      setIsUpdating(false);
      // Refresh rows/history
      const latestHist = await updatestocksApi();
      setHistory(Array.isArray(latestHist) ? latestHist : []);
    } catch {
      setIsUpdating(false);
      alert("Failed to add new dividend amount");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setIsUpdating(true);
    try {
      // Save both price/dividend in one update
      await fetch(`${process.env.REACT_APP_API_URL}/auth/updatestock`, {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stock_id: selected.id,
          cat_id: selected.cat_id,
          current_price: currentPrice,
          current_price_date: currentPriceDate,
          sundry_creditors: dividendAmount ?? undefined,
        }),
      });
      setSelected(null);
      setIsUpdating(false);
      // Refresh rows/history
      const latestHist = await updatestocksApi();
      setHistory(Array.isArray(latestHist) ? latestHist : []);
    } catch {
      setIsUpdating(false);
      alert("Update failed");
    }
  };

  // Category filter change still permitted
  const onCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterCategory(e.target.value);
  };

  // Party dropdown changes change the search param (?party=12 or clear). Also sets filterParty state.
  const handlePartyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === "") {
      setFilterParty("");
      searchParams.delete("party");
      setSearchParams(searchParams, { replace: true });
    } else {
      const partyNum = Number(val);
      setFilterParty(partyNum);
      searchParams.set("party", val);
      setSearchParams(searchParams, { replace: true });
    }
    setFilterCategory("");
  };

  // Clear only category filter
  const handleClearFilters = () => {
    setFilterCategory("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-[#F8F9FA] to-[#f3f8ff] py-10 px-2 flex justify-center items-start">
      <div className="w-full max-w-6xl">
        <h1 className="text-4xl font-bold text-center text-[#22223b] mb-8 tracking-tight">
          <span className="bg-gradient-to-tr from-[#ff9500] to-[#1cab70] bg-clip-text text-transparent">
            Update Stock Value
          </span>
        </h1>
        {/* Filter panel */}
        <div className="flex flex-wrap gap-3 mb-8 items-center justify-center bg-white/60 border rounded-xl shadow px-6 py-4 backdrop-blur-md">
          {/* Always show party dropdown. Default is party param from url, else 'All Parties' */}
          <div className="flex flex-col min-w-[170px]">
            <label className="mb-1 text-sm font-semibold text-[#51547b]">
              Party
            </label>
            <select
              value={filterParty}
              onChange={handlePartyChange}
              className="border rounded-lg px-3 py-2 bg-white focus:border-[#1CAB70] focus:ring-2 focus:ring-[#1CAB70]/10 outline-none transition"
            >
              <option value="">All Parties</option>
              {/* Use parties to display party name */}
              {uniqueParties.map((party) => (
                <option key={party.party_id} value={party.party_id}>
                  {party.party_name && party.party_name.trim()
                    ? party.party_name
                    : `Party #${party.party_id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col min-w-[170px]">
            <label className="mb-1 text-sm font-semibold text-[#51547b]">
              Category
            </label>
            <select
              value={filterCategory}
              onChange={onCategoryChange}
              className="border rounded-lg px-3 py-2 bg-white focus:border-[#1CAB70] focus:ring-2 focus:ring-[#1CAB70]/10 outline-none transition"
            >
              <option value="">All Categories</option>
              {uniqueCategories.map((cat, idx) => (
                <option key={cat + idx} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          {filterCategory && (
            <button
              onClick={handleClearFilters}
              className="self-end px-4 py-2 rounded bg-[#ffecd2] text-[#f85912] font-semibold border border-[#ffcd90] hover:bg-[#fff7ec] flex items-center gap-1 transition"
            >
              <span>Clear Filters</span>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                viewBox="0 0 20 20"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 6l8 8M6 14L14 6"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Table */}
        <div className="shadow-xl border rounded-xl bg-white/70 p-6 backdrop-blur-lg">
          <div className="overflow-x-auto rounded-lg">
            <table className="min-w-full table-fixed rounded-lg shadow border text-base">
              <thead>
                <tr className="bg-[#f3f8ff] border-b-2 border-[#dedede]">
                  <th className="px-4 py-3 font-semibold text-[#22223b] tracking-wider">
                    Party
                  </th>
                  <th className="px-4 py-3 font-semibold text-[#22223b] tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 font-semibold text-[#22223b] tracking-wider">
                    Stock Name
                  </th>
                  <th className="px-4 py-3 font-semibold text-[#22223b] tracking-wider">
                    Unit
                  </th>
                  <th className="px-4 py-3 font-semibold text-[#22223b] tracking-wider">
                    Price/Unit
                  </th>
                  <th className="px-4 py-3 font-semibold text-[#22223b] tracking-wider">
                    Current Price
                  </th>
                  <th className="px-4 py-3 font-semibold text-[#22223b] tracking-wider">
                    Price Date
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-8 text-[#8c98ad] text-base"
                    >
                      No stocks available for selected filter.
                    </td>
                  </tr>
                )}
                {filteredRows.map((stk, idx) => (
                  <tr
                    key={`${stk.id}-${stk.cat_id}-${stk.party_id}`}
                    className={`transition hover:bg-[#fff4de] ${
                      idx % 2 === 1 ? "bg-[#fafcff]" : ""
                    }`}
                  >
                    <td className="px-4 py-3 border-b border-[#ececec] font-semibold text-[#4b5267]">
                      {getPartyNameById(stk.party_id)}
                    </td>
                    <td className="px-4 py-3 border-b border-[#ececec] font-medium">
                      {stk.category || (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 border-b border-[#ececec] ">
                      {stk.name || <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-4 py-3 border-b border-[#ececec]">
                      {stk.unit || <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-4 py-3 border-b border-[#ececec] text-[#1cab70] font-semibold">
                      {stk.price_per_unit ? (
                        `₹${Number(stk.price_per_unit).toLocaleString(
                          "en-IN",
                          {
                            maximumFractionDigits: 2,
                          }
                        )}`
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 border-b border-[#ececec] text-[#ff9500] font-semibold">
                      {stk.current_price ? (
                        `₹${Number(stk.current_price).toLocaleString(
                          "en-IN",
                          { maximumFractionDigits: 2 }
                        )}`
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 border-b border-[#ececec]">
                      {stk.current_price_date
                        ? (() => {
                            if (typeof stk.current_price_date === "string") {
                              const n = new Date(stk.current_price_date);
                              return isNaN(n.getTime())
                                ? stk.current_price_date
                                : n.toLocaleDateString("en-GB");
                            }
                            try {
                              return new Date(
                                stk.current_price_date
                              ).toLocaleDateString("en-GB");
                            } catch {
                              return "";
                            }
                          })()
                        : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-4 py-3 border-b border-[#ececec] text-center">
                      <button
                        className="px-5 py-2 shadow-sm rounded-lg bg-gradient-to-tr from-[#FF9500] to-[#1CAC70] text-white font-semibold text-sm hover:from-[#e28500] hover:to-[#148c59] transition active:scale-95 transform"
                        onClick={() => handleEdit(stk)}
                        disabled={false}
                      >
                        Update
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Update Modal/Panel */}
          {selected && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/35 backdrop-blur-sm">
              <form
                className="w-full max-w-[40rem] bg-white rounded-2xl shadow-xl border-2 border-[#E6E6E6] p-8 animate-fadeIn relative"
                onSubmit={handleSubmit}
                style={{ animation: "fadeIn 0.2s" }}
              >
                <button
                  type="button"
                  className="absolute top-3 right-3 text-gray-400 hover:text-[#1CAC70] text-xl focus:outline-none"
                  onClick={() => setSelected(null)}
                  disabled={isUpdating}
                  aria-label="Close"
                >
                  ×
                </button>
                <h2 className="text-2xl font-bold bg-gradient-to-b from-[#1CAC70] to-[#FF9500] bg-clip-text text-transparent mb-3 text-center tracking-tight">
                  Update Stock Value
                </h2>
                <div className="grid grid-cols-1 gap-2 mb-3 text-center text-base">
                  <div className="mb-1">
                    <span className="font-semibold text-[#667]">Party: </span>
                    <span className="font-bold text-[#37ACB1]">
                      {getPartyNameById(selected.party_id)}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-[#6B7280]">
                      Category:{" "}
                    </span>
                    <span className="font-bold text-[#1CAC70]">
                      {selected.category}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-[#6B7280]">
                      Stock Name:{" "}
                    </span>
                    <span className="font-bold text-[#FF9500]">
                      {selected.name}
                    </span>
                  </div>
                </div>
                <div className="w-full mb-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Current Price */}
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-sm font-semibold mb-1 text-[#22223b] block">
                        Current Price
                      </label>
                      <input
                        className="w-full border focus:border-[#1CAC70] focus:ring-2 focus:ring-[#1CAC70]/30 transition rounded-lg px-4 py-2 text-base outline-none"
                        type="number"
                        step="any"
                        value={currentPrice}
                        onChange={(e) => setCurrentPrice(e.target.value)}
                        required
                        min={0}
                        placeholder="₹0.00"
                      />
                    </div>
                    {/* Dividend Amount */}
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-sm font-semibold mb-1 text-[#22223b] block">
                        Add Dividend Amount
                      </label>
                      <form className="flex flex-row items-center gap-2" onSubmit={handleAddDividend}>
                        <input
                          className="w-full border focus:border-[#1CAC70] focus:ring-2 focus:ring-[#1CAC70]/30 transition rounded-lg px-4 py-2 text-base outline-none"
                          type="number"
                          step="any"
                          value={dividendAmount}
                          onChange={(e) => setDividendAmount(e.target.value)}
                          placeholder="₹0.00"
                          min={0}
                          disabled={isUpdating}
                        />
                        
                      </form>
                    </div>
                    {/* Price Date */}
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-sm font-semibold mb-1 text-[#22223b] block">
                        Price Date
                      </label>
                      <input
                        className="w-full border focus:border-[#1CAC70] focus:ring-2 focus:ring-[#1CAC70]/30 transition rounded-lg px-4 py-2 text-base outline-none"
                        type="date"
                        value={currentPriceDate}
                        onChange={(e) => setCurrentPriceDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
                {/* Show previous dividend history */}
                {sundryHistory.length > 0 && (
                  <div className="my-6">
                    <div className="font-semibold text-[#63798d] mb-2 text-left">
                      Previous Dividend Amounts
                      <span className="ml-2 bg-[#fff4de] text-[#e28500] rounded px-2 py-0.5 text-xs font-bold">{sundryHistory.length}</span>
                    </div>
                    
                    {/* Alternative UI: List view instead of table, no Entry ID */}
                    <ul className="overflow-x-auto max-h-48 border border-[#eee] rounded-xl bg-[#fafcff] divide-y">
                      {sundryHistory.map((h, idx) => (
                        <li
                          key={h.id || idx}
                          className={`flex items-center gap-8 px-3 py-2 ${idx % 2 === 1 ? "bg-[#fff7ee]" : ""}`}
                        >
                          <div className="flex-1 text-[#188006] font-mono font-bold">
                            ₹{Number(h.sundry_creditors).toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </div>
                          <div className="flex-shrink-0 text-[#333]">
                            {(() => {
                              let dt =
                                h.current_price_date ||
                                h.created_at ||
                                "";
                              if (!dt) return <span>-</span>;
                              let date = new Date(dt);
                              if (isNaN(date.getTime())) return dt;
                              return date.toLocaleDateString("en-GB");
                            })()}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex gap-4 mt-8">
                  <button
                    type="submit"
                    className="w-1/2 rounded-lg bg-gradient-to-tr from-[#1CAC70] to-[#FF9500] text-white px-4 py-3 font-semibold shadow hover:from-[#15955c] hover:to-[#d87c00] transition active:scale-95"
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <span>
                        <span className="inline-block h-4 w-4 border-t-2 border-l-2 border-[#FFF] border-dashed rounded-full animate-spin align-[-0.125em] mr-2"></span>
                        Updating...
                      </span>
                    ) : (
                      "Update"
                    )}
                  </button>
                  <button
                    type="button"
                    className="w-1/2 rounded-lg border border-gray-300 bg-[#f9f9f9] text-[#333] px-4 py-3 font-semibold hover:bg-gray-200 shadow transition active:scale-95"
                    onClick={() => setSelected(null)}
                    disabled={isUpdating}
                  >
                    Cancel
                  </button>
                </div>
                <div className="absolute left-0 bottom-0 w-full h-[5px] bg-gradient-to-r from-[#FF9500] via-[#1CAC70] to-[#1CAC70] rounded-b-2xl animate-pulse" />
              </form>
            </div>
          )}
        </div>
      </div>
      <style>
        {`
          @keyframes fadeIn {
            0% { opacity: 0; transform: scale(0.98);}
            100% { opacity: 1; transform: scale(1);}
          }
        `}
      </style>
    </div>
  );
}

export default UpdateStockValuePage;